import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Input,
  Progress,
  useToast,
  Heading,
  Card,
  CardBody,
  Flex,
  Image,
  FormLabel,
  SimpleGrid
} from '@chakra-ui/react';
import { Upload, Trash2 } from 'lucide-react';
import { uploadPresenterPhoto, getPresenterPhotoUrl, checkPresenterPhotoExists } from '../firebase/presenterPhoto';
import { presentationAuth } from '../firebase/config';

const PresenterPhotoManager: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [presenterPhotoUrl, setPresenterPhotoUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [photoExists, setPhotoExists] = useState<boolean>(false);
  const toast = useToast();

  useEffect(() => {
    const fetchPresenterPhoto = async () => {
      try {
        // First check if the presenter photo exists in Firebase Storage
        const exists = await checkPresenterPhotoExists();
        setPhotoExists(exists);
        console.log('[ADMIN] Presenter photo exists in Firebase Storage:', exists);
        
        // Then get the URL (either from Firebase or local fallback)
        const url = await getPresenterPhotoUrl();
        setPresenterPhotoUrl(url);
        console.log('[ADMIN] Presenter photo URL:', url);
      } catch (error) {
        console.error('[ADMIN] Error fetching presenter photo:', error);
        toast({
          title: 'Error fetching presenter photo',
          description: 'There was an error fetching the presenter photo. ' + (error instanceof Error ? error.message : ''),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPresenterPhoto();
  }, [toast]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Check if file is an image
      if (!file.type.includes('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select an image file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to upload.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      console.log('[ADMIN] Starting upload process with file:', selectedFile.name);
      
      // Upload to Firebase
      const downloadUrl = await uploadPresenterPhoto(selectedFile, (progress) => {
        console.log(`[ADMIN] Setting upload progress: ${progress}`);
        setUploadProgress(progress);
      });

      console.log('[ADMIN] Upload completed successfully, new URL:', downloadUrl);
      setPresenterPhotoUrl(downloadUrl);
      setPhotoExists(true);

      // Get current user
      const user = presentationAuth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to upload a presenter photo');
      }
      
      console.log('[PRESENTER PHOTO] Uploading as user:', user.email);
      console.log('[PRESENTER PHOTO] User ID:', user.uid);
      
      // Log user's token to check admin claim
      const idTokenResult = await user.getIdTokenResult(true);
      console.log('[PRESENTER PHOTO] User claims:', idTokenResult.claims);

      toast({
        title: 'Upload successful',
        description: 'Presenter photo has been uploaded successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset state
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('[ADMIN] Error uploading presenter photo:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading the presenter photo. ' + (error instanceof Error ? error.message : ''),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box>
      <Heading size="md" mb={4}>Presenter Photo Management</Heading>
      
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Card>
          <CardBody>
            <Heading size="sm" mb={3}>Current Presenter Photo</Heading>
            {isLoading ? (
              <Text>Loading...</Text>
            ) : presenterPhotoUrl ? (
              <Box textAlign="center">
                <Image 
                  src={presenterPhotoUrl} 
                  alt="Presenter Photo" 
                  maxH="200px"
                  borderRadius="full"
                  mx="auto"
                  border="2px solid"
                  borderColor="blue.500"
                />
                <Text mt={2} fontSize="sm" color="gray.500">
                  This photo will appear in the PresenterBioSlide
                </Text>
                {photoExists ? (
                  <Text mt={1} fontSize="xs" color="green.500">
                    ✓ Photo is stored in Firebase Storage
                  </Text>
                ) : (
                  <Text mt={1} fontSize="xs" color="orange.500">
                    ⚠️ Using local fallback image
                  </Text>
                )}
              </Box>
            ) : (
              <Text>No presenter photo has been uploaded yet.</Text>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Heading size="sm" mb={3}>Upload New Photo</Heading>
            <VStack spacing={4} align="stretch">
              <FormLabel htmlFor="photo-upload">Select Image</FormLabel>
              <Input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isUploading}
                p={1}
              />
              
              {selectedFile && (
                <Text fontSize="sm">
                  Selected file: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                </Text>
              )}
              
              {isUploading && (
                <Box>
                  <Text mb={1} fontSize="sm">Uploading: {Math.round(uploadProgress)}%</Text>
                  <Progress value={uploadProgress} size="sm" colorScheme="blue" />
                </Box>
              )}
              
              <Button
                leftIcon={<Upload size={16} />}
                colorScheme="blue"
                onClick={handleUpload}
                isLoading={isUploading}
                loadingText="Uploading..."
                disabled={!selectedFile || isUploading}
              >
                Upload Photo
              </Button>
              
              <Text fontSize="xs" color="gray.500">
                Recommended: Square image (1:1 aspect ratio), at least 400x400 pixels.
                The image will be displayed as a circle in the presentation.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  );
};

export default PresenterPhotoManager;
